import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, Mail, ArrowRight } from 'lucide-react';
import { cn } from '../../utils/cn';
import { COMPANY_NAME } from '../../constants';
import { SidebarDrawer } from './SidebarDrawer';

// Import logo
import FullLogo from '../../assets/images/Full_logo.png';
// Fluid-sphere animation used as the header's Ask-AI CTA.
import fluidSphere from '../../assets/dynamic_fluid_sphere.gif';

const ASK_AI_SECTION_ID = 'ask-istatec-ai';

export const Header: React.FC = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Hover-intent open for the hamburger.
   *
   * We want the drawer to open when the visitor hovers the three-line
   * icon — no click required — but a raw `onMouseEnter → open` fires
   * even when the mouse just skims across the header on its way to
   * somewhere else, which reads as jumpy. A short 120 ms delay
   * (industry-standard for mega-menu "hover intent") filters out
   * those incidental passes: only a hover that lingers on the button
   * actually opens the drawer.
   *
   * Touch devices don't fire mouseenter, so the click handler still
   * owns that path and nothing changes for phones/tablets. Keyboard
   * users hit Enter/Space on the focused button, which the browser
   * translates into a click, so accessibility is preserved too.
   */
  const openTimerRef = useRef<number | null>(null);

  const scheduleDrawerOpen = useCallback(() => {
    // If it's already open, or we already have a timer running, do
    // nothing — schedule-open is idempotent.
    if (isDrawerOpen || openTimerRef.current !== null) return;
    openTimerRef.current = window.setTimeout(() => {
      setIsDrawerOpen(true);
      openTimerRef.current = null;
    }, 120);
  }, [isDrawerOpen]);

  const cancelPendingDrawerOpen = useCallback(() => {
    if (openTimerRef.current !== null) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  }, []);

  // Clean up any pending timer if the Header unmounts mid-hover
  // (e.g. hot-reload during dev, route-driven layout swap). Prevents
  // "state update on unmounted component" warnings and stray opens.
  useEffect(() => {
    return () => cancelPendingDrawerOpen();
  }, [cancelPendingDrawerOpen]);

  /**
   * "Ask AI" CTA — scroll to the AskIstatecAI section on the home page.
   * If we're already on `/`, smooth-scroll in place. Otherwise navigate to
   * `/#ask-istatec-ai` and let the router (see ScrollToTop) hop to it.
   */
  const goToAskAI = useCallback(
    (extraOnClick?: () => void) => (e: React.MouseEvent) => {
      e.preventDefault();
      extraOnClick?.();
      if (location.pathname === '/') {
        const el = document.getElementById(ASK_AI_SECTION_ID);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // Keep the hash in the URL for shareability without triggering a re-render.
          window.history.replaceState(null, '', `#${ASK_AI_SECTION_ID}`);
          return;
        }
      }
      navigate(`/#${ASK_AI_SECTION_ID}`);
    },
    [navigate, location.pathname],
  );

  return (
    <>
      <header
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
          'bg-black text-white',
        )}
      >
        {/* Full-bleed: the header spans the entire viewport width so the
            hamburger sits at the far left edge and the Ask AI / Let's Connect
            buttons sit at the far right edge (matching the Zensar-style
            reference). Only a small breathing-room padding on the sides. */}
        <div className="w-full px-4 sm:px-6">
          {/* Compact columns keep all controls visible on phones; equal
              desktop columns center the logo. */}
          <div className="grid grid-cols-[44px_minmax(0,1fr)_auto] sm:grid-cols-3 gap-2 items-center h-20">
            {/* ───────────────────── LEFT: hamburger ───────────────────── */}
            <div className="flex justify-start items-center">
              <button
                type="button"
                onClick={() => {
                  // Cancel any queued hover-open — the click handled
                  // it explicitly, and we don't want a stale timer to
                  // fire and toggle state twice.
                  cancelPendingDrawerOpen();
                  setIsDrawerOpen(true);
                }}
                onMouseEnter={scheduleDrawerOpen}
                onMouseLeave={cancelPendingDrawerOpen}
                aria-label="Open navigation menu"
                aria-expanded={isDrawerOpen}
                className={cn(
                  'inline-flex items-center justify-center w-10 h-10 rounded-lg',
                  'text-white/90 hover:text-white hover:bg-white/10',
                  'transition-colors',
                )}
              >
                <Menu className="w-7 h-7" strokeWidth={2.75} />
              </button>
            </div>

            {/* ─────────────────────── CENTER: logo ─────────────────────── */}
            <div className="flex justify-center items-center">
              <Link to="/" aria-label={`${COMPANY_NAME} home`} className="group">
                <img
                  src={FullLogo}
                  alt={COMPANY_NAME}
                  className={cn(
                    'h-auto max-h-9 max-w-[130px] sm:max-w-none sm:max-h-none sm:h-10 md:h-12 object-contain rounded-lg',
                    'transition-transform group-hover:scale-105',
                  )}
                />
              </Link>
            </div>

            {/* ─────────────── RIGHT: Ask AI · Mail · Let's Connect ─────────────── */}
            <div className="flex justify-end items-center gap-2 md:gap-3">
              {/* Ask AI — sphere + optional label */}
              <a
                href={`/#${ASK_AI_SECTION_ID}`}
                onClick={goToAskAI()}
                aria-label="Ask Istatec AI"
                title="Ask Istatec AI"
                className={cn(
                  'group relative inline-flex items-center gap-2 rounded-full',
                  'pl-1 pr-1 lg:pl-1.5 lg:pr-4 py-1',
                  'bg-white/5 hover:bg-white/15 border border-white/15 hover:border-white/30',
                  'transition-all',
                )}
              >
                <span className="relative flex items-center justify-center w-9 h-9 rounded-full overflow-hidden bg-navy-900 ring-1 ring-white/20">
                  <img
                    src={fluidSphere}
                    alt=""
                    aria-hidden="true"
                    className="w-9 h-9 object-cover"
                  />
                </span>
                <span className="hidden lg:inline text-sm font-medium text-white/90 group-hover:text-white">
                  Ask AI
                </span>
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-0 rounded-full blur-md opacity-0 group-hover:opacity-40 bg-accent-500/40 transition-opacity"
                />
              </a>

              {/* Merged Mail + "Let's Connect" CTA — single pill containing
                  the mail icon, label, and arrow. Styling mirrors the Ask AI
                  pill next to it (same subtle border, same font weight, same
                  hover treatment) so the two CTAs read as a matched pair.
                  Collapses to icon-only on the smallest viewports. */}
              <Link
                to="/contact"
                aria-label="Contact us — Let's Connect"
                title="Let's Connect"
                className={cn(
                  'group inline-flex items-center gap-2 rounded-full',
                  'px-2.5 lg:pl-3 lg:pr-4 h-10',
                  'bg-white/5 hover:bg-white/15 border border-white/15 hover:border-white/30',
                  'text-white/90 hover:text-white',
                  'transition-all',
                )}
              >
                <Mail className="w-5 h-5 shrink-0" />
                <span className="hidden lg:inline text-sm font-medium whitespace-nowrap">
                  Let's Connect
                </span>
                <ArrowRight className="hidden lg:inline w-4 h-4 shrink-0 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* ═════════════════════ Mega-menu sidebar drawer ═════════════════════
          Extracted into its own component; it owns the categories data,
          hover-driven preview switching, and the video player. Keeping
          it out of Header.tsx keeps this file focused on the top bar. */}
      <SidebarDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      />
    </>
  );
};
